// layerVisibilityRegistry — first slice of the HI-1 MapView refactor
// (audit 2026-05-22). Pure additive foundation; no consumer migration
// yet. Once this lands and ships, later sessions can move existing
// styledata-handler ref-calls + MapView's hand-rolled `addLayer`
// blocks into registry entries one at a time without risk.
//
// What this module DOES today:
//
//   1) StyleRebuildRegistry — every layer family registers a single
//      `restore(map)` function. MapView's `styledata` handler iterates
//      the registry instead of hand-listing 13 ref-call lines. New
//      layers auto-restore by just calling registerStyleRebuild() at
//      mount and the matching unregister on unmount. Drift-proof.
//
//   2) RefreshableOverlayRegistry — overlays declare {id, label,
//      refresh()}. LayerSheet iterates this instead of importing 5
//      named refreshXxx functions from MapView. Adding a refreshable
//      overlay = one registry call.
//
//   3) NEW LAYER FAMILY ergonomics — the audit's stated goal is "one
//      canonical answer to: how do I add layer X?". The registry's
//      shape mirrors the audit's recommendation; later batches in
//      HI-1 will migrate the 137 inline `addLayer` calls in MapView
//      into entries here.
//
// What this module does NOT do yet (deliberate scope cut):
//
//   - It does NOT introduce a new pattern for `addSource`/`addLayer`
//     (that's the next 3-5 PRs in the HI-1 batch plan).
//   - It does NOT consume layerCatalog or uiStore directly. The audit
//     suggested folding layerStatusStore into a layerVisibilityStore;
//     deferred so this PR stays pure additive.
//   - It does NOT change any behavior. Empty registries are a no-op;
//     until consumers register entries, MapView keeps doing exactly
//     what it does today.
//
// Why "registry" and not "store": registrations are imperative side-
// effects of MapView mounting. Using a Zustand store here would force
// every layer-mount block to participate in React reactivity for no
// benefit. A plain module-level Map is the right shape.

import type { Map as MlMap } from 'maplibre-gl';
import type { LayerVisibility } from '../stores/layerVisibilityStore';

// ── StyleRebuildRegistry ─────────────────────────────────────────────
//
// One entry per "thing that needs re-applying after a basemap setStyle()
// rebuild". MapView's `styledata` handler currently calls 13 ref-stored
// callbacks (applyTerrainRef, ihogRingsApplyRef, dipSitesApplyRef,
// rangeRingsRebuildRef, airportDiagramRebuildRef, namedPassesRebuildRef,
// waypointsPushRef, etc.). The audit flagged this as the root cause of
// silent dropouts (flightcat circles, surfacewinds, ceilvis, temperature,
// dewspread, GFA, fuel-dots) when a layer family was added but its
// restore was forgotten.
//
// Migration plan (NOT in this PR — kept here for documentation):
//   - Each `addOverlays()` block in MapView calls
//     `registerStyleRebuild('<id>', (map) => restoreThisLayer(map))`
//     at the bottom of its setup.
//   - MapView's styledata handler replaces the 13 hand-listed ref-calls
//     with `runStyleRebuildHandlers(map)`.
//   - The ref pattern can stay alongside until the migration is
//     complete; the registry is purely additive.

export interface StyleRebuildEntry {
  /** Human-readable id (used for debug + dedup). Same string passed
   *  to `registerStyleRebuild` becomes the dedup key, so re-registering
   *  with the same id silently replaces the prior entry. */
  id: string;
  /** Idempotent restore. Called on every basemap setStyle() rebuild
   *  AFTER the new style + sources/layers have been re-added. Should
   *  re-apply visibility, filters, and any GeoJSONSource.setData()
   *  needed to bring the layer back to its current UI state. */
  restore: (map: MlMap) => void;
}

const _styleRebuildEntries = new Map<string, StyleRebuildEntry>();

/** Register (or replace) a style-rebuild restore for a layer family.
 *  Returns an unregister function suitable for use in a React effect
 *  cleanup, but the typical caller is module-level and never unregisters. */
export function registerStyleRebuild(entry: StyleRebuildEntry): () => void {
  _styleRebuildEntries.set(entry.id, entry);
  return () => { _styleRebuildEntries.delete(entry.id); };
}

/** Read-only view of the current registry (for tests + debug overlays). */
export function getStyleRebuildEntries(): ReadonlyArray<StyleRebuildEntry> {
  return [..._styleRebuildEntries.values()];
}

/** Run every registered restore against the given map. Swallows
 *  per-entry exceptions so a single bad layer can't block the rest
 *  of the basemap-rebuild restore chain. Called from MapView's
 *  styledata handler at the end of the rebuild sequence. */
export function runStyleRebuildHandlers(map: MlMap): void {
  for (const entry of _styleRebuildEntries.values()) {
    try {
      entry.restore(map);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[layerRegistry] style-rebuild restore failed for ${entry.id}:`, err);
    }
  }
}

// ── RefreshableOverlayRegistry ───────────────────────────────────────
//
// LayerSheet's manual ↻ button currently hard-imports 5 named refresh
// wrappers from MapView (`refreshFires`, `refreshSua`, `refreshTfrs`,
// `refreshGfaPublic`, `refreshCwaPublic`). Adding a new refreshable
// overlay = adding to all five places: a new refresh function in
// MapView, a new export, a new import in LayerSheet, a new entry in
// the Promise.all, and an entry in this registry. Drift-prone.
//
// New shape: each overlay registers `{ id, label, refresh }`. LayerSheet
// iterates this registry instead of importing named functions.

export interface RefreshableOverlay {
  /** Stable id. Matches OverlayId where applicable. */
  id: string;
  /** Optional display label for debug surfaces (LayerSheet does not
   *  show a per-overlay label today; this is forward-looking). */
  label?: string;
  /** Refresh the overlay. Should pass through to
   *  overlayRuntime.refreshOverlay (or its public-wrapper equivalent
   *  in MapView that also flags the overlay as loaded). Returning a
   *  Promise lets LayerSheet `Promise.all` the click handler. */
  refresh: () => Promise<unknown>;
}

const _refreshableOverlays = new Map<string, RefreshableOverlay>();

/** Register (or replace) a refreshable overlay. Returns an unregister
 *  function for symmetry with `registerStyleRebuild`. */
export function registerRefreshableOverlay(entry: RefreshableOverlay): () => void {
  _refreshableOverlays.set(entry.id, entry);
  return () => { _refreshableOverlays.delete(entry.id); };
}

/** Read-only view of the registry. LayerSheet should call this once
 *  on render and iterate the result. */
export function getRefreshableOverlays(): ReadonlyArray<RefreshableOverlay> {
  return [..._refreshableOverlays.values()];
}

/** Convenience: refresh every registered overlay in parallel. Swallows
 *  per-overlay errors so a single bad fetch doesn't reject the whole
 *  Promise.all (LayerSheet doesn't want a single AWC outage to make
 *  every layer's status look broken). */
export async function refreshAllRegisteredOverlays(): Promise<void> {
  const entries = [..._refreshableOverlays.values()];
  await Promise.all(
    entries.map((e) =>
      e.refresh().catch((err) => {
        // eslint-disable-next-line no-console
        console.warn(`[layerRegistry] refresh failed for ${e.id}:`, err);
      }),
    ),
  );
}

// ── LayerFamilySpec ─ unified shape for HI-1 per-family migrations ─────
//
// The audit synthesis (2026-05-22) called for a single canonical table
// driving style-rebuild restore + toggle effects + LayerSheet refresh
// from one entry per layer family. The shape it recommended:
//
//   { layerIds, sourceId, visibilitySelector, applyFilter?, refresh? }
//
// Batch 9 (this PR) introduces that shape as `LayerFamilySpec` and a
// `registerLayerFamily()` helper that composes the lower-level
// primitives above. Each future batch (5 operational, 6b weather, 7
// hazards, 8 flight-plan) extracts a per-family file that calls
// registerLayerFamily() instead of separately calling
// registerStyleRebuild + registerRefreshableOverlay + adding a
// useEffect in MapView for the visibility toggle.
//
// The lower-level registries (StyleRebuildRegistry +
// RefreshableOverlayRegistry) stay public for cases that don't fit the
// full LayerFamilySpec shape (e.g. wxstations registered just a restore
// in batch 6a because it's a multi-layer cluster sharing one source).

export interface LayerFamilySpec {
  /** Stable id. Used for dedup in both underlying registries. */
  id: string;
  /** Human-readable label for debug surfaces / future LayerSheet rows. */
  label?: string;
  /** The GeoJSON / raster-dem / raster source id this family owns.
   *  Optional for families that don't own a source (e.g. style-only
   *  layers riding on a shared source). */
  sourceId?: string;
  /** Every MapLibre layer id mounted by this family. The Visibility
   *  controller (added later) iterates these to apply 'visible' / 'none'
   *  in bulk; the styledata-restore handler iterates them to verify
   *  presence after a basemap rebuild. */
  layerIds: ReadonlyArray<string>;
  /** Idempotent mount: addSource + addLayer calls. Called once at first
 *  map.on('load') AND on every basemap setStyle() rebuild. Safe to no-op
 *  via `if (!map.getSource(sourceId)) ...` guard. */
  mount: (map: MlMap) => void;
  /** Post-mount restore for filters / state that addOverlays() doesn't
 *  re-apply on its own (e.g. wxstations time-scrubber filter). Runs
 *  AFTER mount, on every basemap rebuild. Optional. */
  applyState?: (map: MlMap) => void;
  /** Manual-refresh handler for LayerSheet's button. Optional — only
 *  families backed by an upstream feed need this. */
  refresh?: () => Promise<unknown>;

  // ── HI-1' additions (2026-05-24) ─────────────────────────────────────

  /** Pure function: given the current layers state, returns whether this
   *  family should be visible. Used by runVisibilityHandlers() to:
   *    1. Drive per-layer setLayoutProperty visibility for families that
   *       don't have their own applyState (simple, uniform toggle).
   *    2. Detect OFF→ON transitions so onToggleOn can be called.
   *    3. Determine whether applyState should be called (when the
   *       family has applyState that handles its own per-layer logic).
   *  Families NOT managed by runVisibilityHandlers (dedicated controllers
   *  like TrafficController, TerrainController, etc.) do NOT set this. */
  visibilitySelector?: (layers: LayerVisibility) => boolean;

  /** Called once when visibility transitions OFF → ON (cold-boot with the
   *  layer already ON counts as OFF→ON on first runVisibilityHandlers call).
   *  Use for lazy data fetches / data hydration. Called AFTER applyState
   *  (or the visibility flip) so the layers are visible before the fetch
   *  starts. */
  onToggleOn?: (map: MlMap) => Promise<unknown> | void;

  /** Called from MapView's moveend handler (debounced, ~200ms) when a
   *  new viewport bbox is available. Use for viewport-scoped data fetches
   *  (classBCD, airways, sua, viirs, pireps, fires, etc.).
   *  Only called when the family has been toggled on (isOverlayLoaded or
   *  the family's store flag is true — implementations should gate on
   *  their own loaded/enabled check). */
  bboxRefresh?: (
    map: MlMap,
    bbox: { w: number; s: number; e: number; n: number },
    zoom: number,
  ) => Promise<unknown> | void;
}

// ── LayerFamilySpec registry ── full-spec storage for HI-1' visibility/bbox

/** Full-spec registry: id → LayerFamilySpec. Keyed by id. */
const _layerFamilies = new Map<string, LayerFamilySpec>();

/** Visibility-state tracker for OFF→ON transition detection.
 *  null = first run (treat as OFF for onToggleOn purposes). */
const _visibilityState = new Map<string, boolean | null>();

/** Register a layer family with the unified spec. Composes the two
 *  underlying registries so a single call provides:
 *    - basemap-rebuild restore (mount + applyState)
 *    - LayerSheet refresh participation (if refresh given)
 *    - visibility-handler + bbox-refresh participation
 *
 *  Returns an unregister function that drops both underlying entries.
 *  Calling registerLayerFamily again with the same id replaces the prior
 *  entry in both underlying registries.
 */
export function registerLayerFamily(spec: LayerFamilySpec): () => void {
  _layerFamilies.set(spec.id, spec);
  const unregisterRestore = registerStyleRebuild({
    id: spec.id,
    restore: (map) => {
      spec.mount(map);
      spec.applyState?.(map);
    },
  });
  const unregisterRefresh = spec.refresh
    ? registerRefreshableOverlay({ id: spec.id, label: spec.label, refresh: spec.refresh })
    : () => {};
  return () => {
    _layerFamilies.delete(spec.id);
    _visibilityState.delete(spec.id);
    unregisterRestore();
    unregisterRefresh();
  };
}

/** Look up a registered family by id (for test + debug). */
export function getLayerFamily(id: string): LayerFamilySpec | undefined {
  return _layerFamilies.get(id);
}

/** Apply current visibility state to every registered family that opted in
 *  via visibilitySelector. Called from MapView's layers-change useEffect.
 *
 *  Design:
 *  - Families with applyState: delegate to applyState (handles per-layer
 *    logic, filters, tile URLs, etc. — more capable than a generic loop).
 *  - Families without applyState: bulk-apply uniform visibility to layerIds.
 *  - OFF→ON transition: call onToggleOn (lazy fetch / data hydration).
 *  - Errors from any single family are swallowed so one bad family can't
 *    block the rest of the visibility pass.
 */
export function runVisibilityHandlers(
  map: MlMap,
  layers: LayerVisibility,
): void {
  for (const [id, family] of _layerFamilies.entries()) {
    if (!family.visibilitySelector) continue;

    const wantVisible = family.visibilitySelector(layers);
    const prevVisible = _visibilityState.get(id) ?? null;

    try {
      if (family.applyState) {
        // applyState owns full per-layer visibility + filter logic.
        // (GFA, wxstations, sigmet, cwa, etc. have compound selectors
        // or per-layer filter state that needs more than a bulk flip.)
        family.applyState(map);
      } else {
        // Simple families: uniform visibility for all layerIds.
        const v = wantVisible ? 'visible' : 'none';
        for (const layerId of family.layerIds) {
          if (!map.getLayer(layerId)) continue;
          map.setLayoutProperty(layerId, 'visibility', v);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[layerRegistry] visibility handler failed for ${id}:`, err);
    }

    // Call onToggleOn on OFF→ON transition (null = first run, counts as OFF).
    if (wantVisible && prevVisible !== true) {
      try {
        family.onToggleOn?.(map);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[layerRegistry] onToggleOn failed for ${id}:`, err);
      }
    }

    _visibilityState.set(id, wantVisible);
  }
}

/** Reset visibility state for all families. Call when the map is torn down
 *  (component unmount) so the next mount treats everything as first-run. */
export function resetVisibilityState(): void {
  _visibilityState.clear();
}

/** Iterate bboxRefresh handlers for the current viewport. Called from
 *  MapView's moveend handler (debounced). Swallows per-family errors. */
export async function runBboxRefreshHandlers(
  map: MlMap,
  bbox: { w: number; s: number; e: number; n: number },
  zoom: number,
): Promise<void> {
  const promises: Array<Promise<unknown>> = [];
  for (const family of _layerFamilies.values()) {
    if (!family.bboxRefresh) continue;
    try {
      const p = family.bboxRefresh(map, bbox, zoom);
      if (p) promises.push(p);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[layerRegistry] bboxRefresh failed for ${family.id}:`, err);
    }
  }
  if (promises.length > 0) {
    await Promise.all(promises.map((p) => p.catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[layerRegistry] bboxRefresh promise rejected:', err);
    })));
  }
}
